import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { LayoutDashboard, Users, Wallet, CreditCard, Gift, Tag, Bell, LifeBuoy, FileBarChart, BarChart3, ScrollText, Settings, LogOut, Menu, Palette, Target, Percent, Rocket, ShieldCheck, Sparkles, MessageSquareHeart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AdminAccessProvider, useAdminAccess } from "@/context/AdminAccess";
import { ForcedPasswordChange } from "@/components/ForcedPasswordChange";
import { cn } from "@/lib/utils";
import { Spinner } from "@/components/common";
import { LogoMark } from "@/components/Logo";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";

const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, perm: "dashboard.view" },
  { to: "/admin/customers", label: "Customers", icon: Users, perm: "customers.view" },
  { to: "/admin/loans", label: "Loans", icon: Wallet, perm: "loans.view" },
  { to: "/admin/payments", label: "Payments", icon: CreditCard, perm: "payments.view" },
  { to: "/admin/collection", label: "Collection Intel", icon: Target, perm: "collection.view" },
  { to: "/admin/rewards", label: "Rewards", icon: Gift, perm: "wallet_rewards.view" },
  { to: "/admin/reward-rules", label: "Reward Rules", icon: Percent, perm: "reward_rules.manage" },
  { to: "/admin/offers", label: "Offers", icon: Tag, perm: "offers.view" },
  { to: "/admin/support", label: "Support", icon: LifeBuoy, perm: "support.view" },
  { to: "/admin/reports", label: "Reports", icon: FileBarChart, perm: "reports.view" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, perm: "analytics.view" },
  { to: "/admin/askai/insights", label: "Ask AI Insights", icon: Sparkles, perm: "customer_voice.view" },
  { to: "/admin/customer-voice", label: "Customer Voice", icon: MessageSquareHeart, perm: "customer_voice.view" },
  { to: "/admin/audit-logs", label: "Audit Logs", icon: ScrollText, perm: "audit.view" },
  { to: "/admin/admins", label: "Admin Management", icon: ShieldCheck, super: true },
  { to: "/admin/settings", label: "Settings", icon: Settings, perm: "settings.view" },
  { to: "/admin/production-readiness", label: "Go-Live Check", icon: Rocket, perm: "production_readiness.view" },
  { to: "/admin/brand-kit", label: "Brand Kit", icon: Palette, perm: null },
];

// Module-level (stable identity) so route changes never remount the sidebar → scroll is
// preserved. Scroll position persisted in a module var and restored across remounts.
let _adminNavScroll = 0;
function AdminSideNav({ items, active, onLogout }) {
  const ref = useRef(null);
  useLayoutEffect(() => { if (ref.current) ref.current.scrollTop = _adminNavScroll; });
  return (
    <>
      <div className="flex h-16 items-center gap-2 border-b border-white/10 px-6">
        <LogoMark size={34} className="rounded-[10px] shadow-sm" />
        <div><p className="font-display text-sm font-extrabold text-white">ZRo<span className="text-[#14B8A6]"> Pe</span></p><p className="-mt-1 text-[10px] font-semibold uppercase tracking-widest text-[#14B8A6]">Admin</p></div>
      </div>
      <nav ref={ref} onScroll={(e) => { _adminNavScroll = e.currentTarget.scrollTop; }}
        className="flex-1 space-y-0.5 overflow-y-auto scroll-thin px-3 py-4">
        {items.map((n) => (
          <Link key={n.to} to={n.to} data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
            className={cn("flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              active(n.to) ? "bg-[#14B8A6] text-white" : "text-slate-300 hover:bg-white/5 hover:text-white")}>
            <n.icon size={17} /> {n.label}
          </Link>
        ))}
      </nav>
      <div className="border-t border-white/10 p-3">
        <button onClick={onLogout} data-testid="admin-logout"
          className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-slate-300 hover:bg-white/5">
          <LogOut size={17} /> Logout
        </button>
      </div>
    </>
  );
}

function AdminShellInner() {
  const { user, logout } = useAuth();
  const { access, can, isSuper } = useAdminAccess();
  const loc = useLocation();
  const nvg = useNavigate();
  const [open, setOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const active = (to) => loc.pathname === to;

  const items = (access && !access.must_change_password)
    ? NAV.filter((n) => (n.super ? isSuper : (!n.perm || can(n.perm))))
    : [];

  // Redirect away from the default dashboard if this admin lacks dashboard access.
  useEffect(() => {
    if (!access || access.must_change_password) return;
    const landing = loc.pathname === "/admin" || loc.pathname === "/admin/dashboard";
    if (landing && !can("dashboard.view") && items.length) nvg(items[0].to, { replace: true });
  }, [access, loc.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  if (access === null) return <div className="min-h-screen bg-slate-50"><Spinner label="Loading admin" /></div>;
  if (access && access.must_change_password) return <ForcedPasswordChange />;


  return (
    <div className="min-h-screen bg-slate-100">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 flex-col navy-grain lg:flex"><AdminSideNav items={items} active={active} onLogout={() => setLogoutOpen(true)} /></aside>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-60 flex-col navy-grain" onClick={() => setOpen(false)}><AdminSideNav items={items} active={active} onLogout={() => setLogoutOpen(true)} /></aside>
        </div>
      )}
      <div className="lg:pl-60">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white px-4 lg:px-8">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="admin-menu"><Menu /></button>
            <h1 className="font-display font-bold text-[#0B132B]">Admin Console</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="rounded-md bg-[#0B132B] px-2.5 py-1 text-xs font-semibold text-[#14B8A6]" data-testid="admin-role-badge">{user?.role}</span>
            <span className="hidden text-sm text-slate-600 sm:block">{user?.email}</span>
          </div>
        </header>
        <main className="admin-console mx-auto max-w-[1400px] px-4 py-6 lg:px-8"><Outlet /></main>
      </div>
      <AlertDialog open={logoutOpen} onOpenChange={setLogoutOpen}>
        <AlertDialogContent data-testid="admin-logout-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Logout?</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to logout from ZRo Pe?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-logout-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-logout-confirm" onClick={() => { logout(); nvg("/admin/login"); }}>Logout</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function AdminShell() {
  return (
    <AdminAccessProvider>
      <AdminShellInner />
    </AdminAccessProvider>
  );
}
